import { Check } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * Horizontal numbered stepper above the onboarding cards. Mirrors the
 * fresh-dashboard `PreTrialScreen` stepper: numbered circles connected by a
 * thin line, the previous-completed line going primary, the next-incomplete
 * line staying on `border`. Active circle is filled primary; completed
 * circles render the lucide `Check` glyph on success-soft.
 *
 * Pass `onStepClick(index)` to make navigable steps interactive — the page
 * uses this to let users jump back to a confirmed step. Only steps that are
 * `done` or `clickable` render as buttons; pending steps stay inert.
 */
export function OnboardingStepper({ steps, currentStep, viewingStep, onStepClick }) {
	return (
		<div className="mb-10 flex items-start" aria-label="Onboarding progress">
			{steps.map((step, index) => {
				const isDone = Boolean(step.done);
				const isActive = index === currentStep && !isDone;
				const isViewing = viewingStep === index;
				const prevDone = index > 0 && steps[index - 1].done;
				const navigable = typeof onStepClick === 'function' && (isDone || index === currentStep);

				const circle = (
					<div
						className={cn(
							'relative z-10 flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold transition-shadow',
							isDone
								? 'bg-success-soft text-success-soft-foreground'
								: isActive
									? 'bg-primary text-primary-foreground'
									: 'border border-border bg-background text-muted-foreground',
							isViewing ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : null
						)}
					>
						{isDone ? <Check className="h-4 w-4" /> : index + 1}
					</div>
				);

				return (
					<div key={step.id} className="relative flex flex-1 flex-col items-center">
						{index > 0 ? (
							<div
								className={cn('absolute right-1/2 top-5 h-px w-full', prevDone ? 'bg-primary' : 'bg-border')}
								style={{ marginRight: 20 }}
							/>
						) : null}
						{navigable ? (
							<button
								type="button"
								onClick={() => onStepClick(index)}
								className="relative z-10 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
								aria-label={`Go to ${step.shortLabel}`}
							>
								{circle}
							</button>
						) : (
							circle
						)}
						<div
							className={cn(
								'mt-2 text-center text-xs',
								isActive || isViewing
									? 'font-semibold text-foreground'
									: isDone
										? 'text-foreground'
										: 'text-muted-foreground'
							)}
						>
							{step.shortLabel}
						</div>
					</div>
				);
			})}
		</div>
	);
}
